import moment from 'moment';
import { FXPServiceCatalog, FxpServiceFactory } from '@fxp/fxpservices-di';
import { FxpHttpService, FxpMessageService } from '@fxp/fxpservices';
import { Constants } from '../../common/constants';
import { RAvailUrlProvider } from '../../common/RAvailUrlProvider';
import CalendarService from './Calendar.service';
import { CERecommendedHrsResponse } from '../../common/Contracts';
import { CalendarEvent } from '../contracts/YearlyCalendarContracts';

export interface NonProjectUpsertRequest {
  nonProject: NonProjectUpdateObject[];
}

export interface NonProjectUpdateObject {
  uxid: string;
  nonProjectRequestId: number;
  isBusy: boolean;
  eventType: string;
  comments: string;
  recurrencePattern: string | null;
  recurrenceEndsBy: string | null;
  recurrenceEndsAfter: number | null;
  isRecurrent: boolean;
  /** true when the series ends on a date ("Ends by"), false for "Ends after N" */
  isRecurrenceEndDate?: boolean;
  technologyId: number;
  technologyName: string;
  resourceAlias: string;
  timeZone: string;
  startDateTime: string;
  endDateTime: string;
}

export interface NonProjectUpdateResponse {
  responseCode?: string;
  statusMessage?: string;
  identifier?: number;
}

export interface YearlyCalendarFetchRequest {
  alias: string;
  startDate: Date;
  endDate: Date;
  fetchProjectEventsByRequestedDates?: boolean;
  /** False on a cross-user calendar — see `fetchTalentRecommendedHours`. Defaults to true. */
  includeOutlookEvents?: boolean;
}

export interface YearlyCalendarCrudOperation {
  action: 'create' | 'update' | 'delete';
  date: Date;
  event?: CalendarEvent | null;
}

export interface GRMEventType {
  EventTypeId: number;
  EventTypeName: string;
  EventColor: string;
  DisplayInEventList: boolean;
  IsBusy: boolean;
  IsCommentRequired: boolean;
  IsApplicableToManagers: boolean;
  IsApplicableToRCs: boolean;
  IsSickVacationTraining: boolean;
  IsSystemGenerated: boolean;
  IsAllDayEvent: boolean;
  IsSpecialtyRequired: boolean;
  IsPrivate: boolean;
  PrivateText: string;
  PrivateTextColor: string;
  IsActive: boolean;
}

interface IYearlyCalendarBridgeService {
  fetchCalendarData: (request: YearlyCalendarFetchRequest) => Promise<CERecommendedHrsResponse>;
  fetchNonProjectEventTypes: () => Promise<GRMEventType[]>;
  getUserPreference: (userAlias: string, type: string) => Promise<string | null>;
  saveUserPreference: (userAlias: string, type: string, preference: string) => Promise<void>;
  upsertNonProjectSchedules: (payload: NonProjectUpsertRequest) => Promise<NonProjectUpdateResponse[]>;
  deleteNonProjectSchedule: (event: CalendarEvent, shouldDeleteSeries?: boolean) => Promise<NonProjectUpdateResponse[]>;
  buildUpsertPayloadFromOperation: (
    op: YearlyCalendarCrudOperation,
    resourceAlias: string,
    standardTimeZone: string
  ) => NonProjectUpsertRequest;
}

const toGrmDateTime = (input: Date | string) =>
  moment(input).format('DD-MMM-YYYY hh:mm A');

const getComments = (eventType: string, comments?: string) => {
  if (comments && comments.trim().length > 0) {
    return comments.trim();
  }
  return eventType;
};

const getEventType = (event?: CalendarEvent | null) => {
  const customProperties = event && event.customProperties ? event.customProperties : null;
  return (customProperties && customProperties.AssignmentType) || event?.eventTypeName || 'Vacation';
};

const getStartEndFromOperation = (op: YearlyCalendarCrudOperation) => {
  if (op.event && op.event.startDt && op.event.endDt) {
    return {
      startDateTime: new Date(op.event.startDt as any),
      endDateTime: new Date(op.event.endDt as any),
    };
  }

  const startDateTime = new Date(
    op.date.getFullYear(),
    op.date.getMonth(),
    op.date.getDate(),
    9,
    0,
    0,
    0
  );
  const endDateTime = new Date(
    op.date.getFullYear(),
    op.date.getMonth(),
    op.date.getDate(),
    17,
    0,
    0,
    0
  );

  return {
    startDateTime,
    endDateTime,
  };
};

export const buildUpsertPayloadFromOperation = (
  op: YearlyCalendarCrudOperation,
  resourceAlias: string,
  standardTimeZone: string
): NonProjectUpsertRequest => {
  const customProperties = op.event && op.event.customProperties ? op.event.customProperties : {};
  const eventType = getEventType(op.event);
  const dateRange = getStartEndFromOperation(op);

  const item: NonProjectUpdateObject = {
    uxid: `${customProperties.Id || 0}`,
    nonProjectRequestId: customProperties.Id || op.event?.eventId || 0,
    isBusy: customProperties.IsBusy !== undefined ? !!customProperties.IsBusy : true,
    eventType,
    comments: getComments(eventType, customProperties.AdditionalProperties?.Comments || customProperties.comments),
    recurrencePattern: customProperties.RecurrencePattern || 'Daily',
    recurrenceEndsBy: customProperties.RecurrenceEndsBy || toGrmDateTime(dateRange.endDateTime),
    recurrenceEndsAfter: customProperties.IsRecurrent ? +(customProperties.RecurrenceEndsAfter || 0) : 0,
    isRecurrent: !!customProperties.IsRecurrent,
    // Preserve the termination mode: honour the explicit flag when present,
    // otherwise infer it from whichever termination field is populated.
    isRecurrenceEndDate: customProperties.IsRecurrenceEndDate !== undefined
      && customProperties.IsRecurrenceEndDate !== null
      ? !!customProperties.IsRecurrenceEndDate
      : !(+(customProperties.RecurrenceEndsAfter || 0) > 0),
    technologyId: 0,
    technologyName: customProperties.AdditionalProperties?.TechnologyName || '',
    resourceAlias,
    timeZone: customProperties.TimeZone || standardTimeZone,
    startDateTime: toGrmDateTime(dateRange.startDateTime),
    endDateTime: toGrmDateTime(dateRange.endDateTime),
  };

  return { nonProject: [item] };
};

/**
 * Build a flat payload matching the GRM Calendar API contract
 * (`api/v1/Calendar/CreateNonProjectRequestAndSchedules` / `UpdateNonProjectSchedules`).
 * This is the proven format used by the old GRM Experience (resourcedashboardv2/edit-event.controller).
 */
export const buildGrmCalendarPayload = (
  op: YearlyCalendarCrudOperation,
  resourceAlias: string,
  standardTimeZone: string
): Record<string, unknown> => {
  const customProperties = op.event && op.event.customProperties ? op.event.customProperties : {} as any;
  const eventType = getEventType(op.event);
  const dateRange = getStartEndFromOperation(op);
  const isUpdate = op.action === 'update' && (customProperties.Id || op.event?.eventId);

  return {
    id: isUpdate ? (customProperties.Id || op.event?.eventId || 0) : 0,
    eventType,
    comments: getComments(eventType, customProperties.AdditionalProperties?.Comments || customProperties.comments),
    startDateTime: toGrmDateTime(dateRange.startDateTime),
    endDateTime: toGrmDateTime(dateRange.endDateTime),
    resourceAlias,
    timeZone: customProperties.TimeZone || standardTimeZone,
    isBusy: true,
    showAs: 'Busy',
    isRecurrent: !!customProperties.IsRecurrent,
    recurrencePattern: customProperties.IsRecurrent
      ? (customProperties.RecurrencePattern || 'Daily')
      : 'None',
    recurrenceEndsAfter: customProperties.IsRecurrent ? +(customProperties.RecurrenceEndsAfter || 0) : null,
    recurrenceEndsBy: customProperties.RecurrenceEndsBy || null,
    technologyId: 0,
    technologyName: customProperties.AdditionalProperties?.TechnologyName || '',
  };
};

const YearlyCalendarBridgeService = (function () {
  let _instance: IYearlyCalendarBridgeService;

  function createInstance(): IYearlyCalendarBridgeService {
    const urlProvider = new RAvailUrlProvider();
    const httpService = FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.FxpHttpService
    ) as FxpHttpService;
    const messageService = FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.FxpMessageService
    ) as FxpMessageService;
    const calendarService = CalendarService.getSingleton();

    const fetchCalendarData = async (
      request: YearlyCalendarFetchRequest
    ): Promise<CERecommendedHrsResponse> => {
      return calendarService.fetchTalentRecommendedHours(
        request.alias,
        request.startDate,
        request.endDate,
        request.fetchProjectEventsByRequestedDates,
        request.includeOutlookEvents
      );
    };

    const fetchNonProjectEventTypes = async (): Promise<GRMEventType[]> => {
      try {
        // Approach 1: GRM RM API — /api/v1/Domain/GetDomainData (POST)
        // This endpoint is on GrmApiBaseUrl which already has auth configured
        const domainDataUrl = `${urlProvider.GrmApiBaseUrl()}${Constants.GrmApiConstants.getDomainDataEndpoint}`;
        const response = await httpService.post(
          domainDataUrl,
          Constants.GrmApiConstants.eventTypesPayload,
          { 'Content-Type': 'application/json' }
        );
        const data = response?.data || response;
        // The response may be wrapped differently depending on the API version:
        // - { DomainDataResult: { EventTypes: [...] } }   (domain data wrapper)
        // - { EventTypes: [...] }                          (direct)
        // - [ {...}, {...} ]                               (raw array)
        let eventTypes: GRMEventType[] = [];
        if (data?.DomainDataResult?.EventTypes) {
          eventTypes = data.DomainDataResult.EventTypes;
        } else if (data?.EventTypes) {
          eventTypes = data.EventTypes;
        } else if (Array.isArray(data)) {
          eventTypes = data;
        }

        const filtered = eventTypes.filter(et =>
          et.DisplayInEventList &&
          et.IsActive &&
          !Constants.BlockedEventTypes.includes(et.EventTypeId) &&
          et.EventTypeName.toLowerCase() !== 'public holiday'
        );
        if (filtered.length > 0) {
          return filtered;
        }
      } catch (_error) {
        // GRM RM endpoint failed — try master data approach
      }

      try {
        // Approach 2: GRM Master Data API — GET /grm/md/api/md?types=EventTypes&isAll=false
        // (same endpoint e360 uses via GrmEventsUrl)
        const grmMdBaseUrl = urlProvider.GrmApiBaseUrl().replace('/grm/rm', '/grm/md');
        const eventTypesUrl = `${grmMdBaseUrl}/api/md?types=EventTypes&isAll=false`;
        const mdResponse = await httpService.get(eventTypesUrl);
        const mdData = mdResponse?.data || mdResponse;
        let mdEventTypes: GRMEventType[] =
          mdData?.DomainDataResult?.EventTypes ||
          mdData?.EventTypes ||
          (Array.isArray(mdData) ? mdData : []);
        return mdEventTypes.filter(et =>
          et.DisplayInEventList &&
          et.IsActive &&
          !Constants.BlockedEventTypes.includes(et.EventTypeId) &&
          et.EventTypeName.toLowerCase() !== 'public holiday'
        );
      } catch (_error2) {
        // Both approaches failed — return empty
        return [];
      }
    };

    /**
     * Read a GRM user preference (calendar.service.js:219-244,
     * POST api/User/ViewUserPreference). Returns null when unset or on failure, so callers apply
     * their own default exactly as legacy does.
     */
    const getUserPreference = async (
      userAlias: string,
      type: string
    ): Promise<string | null> => {
      try {
        const url = `${urlProvider.GrmApiBaseUrl()}${Constants.GrmApiConstants.viewUserPreference}`;
        const response = await httpService.post(
          url,
          { UserAlias: userAlias, Type: type },
          { 'Content-Type': 'application/json' }
        );
        const data = response?.data ?? response;
        return typeof data === 'string' && data.length > 0 ? data : null;
      } catch (_error) {
        return null;
      }
    };

    /**
     * Persist a GRM user preference (calendar.service.js:246-262,
     * POST api/User/SavePreference). Fire-and-forget: legacy only surfaces a soft info message on
     * failure and never blocks the view switch on it.
     */
    const saveUserPreference = async (
      userAlias: string,
      type: string,
      preference: string
    ): Promise<void> => {
      try {
        const url = `${urlProvider.GrmApiBaseUrl()}${Constants.GrmApiConstants.saveUserPreference}`;
        await httpService.post(
          url,
          { UserAlias: userAlias, Type: type, Preference: preference },
          { 'Content-Type': 'application/json' }
        );
      } catch (_error) {
        /* non-blocking, matching legacy */
      }
    };

    const upsertNonProjectSchedules = async (
      payload: NonProjectUpsertRequest
    ): Promise<NonProjectUpdateResponse[]> => {
      try {
        const items = Array.isArray(payload?.nonProject) ? payload.nonProject : [];
        if (items.length === 0) {
          return [];
        }

        const headers = { 'Content-Type': 'application/json' };
        const patchFn = (httpService as any).patch || (httpService as any).put;

        const upsertOne = async (item: NonProjectUpdateObject): Promise<NonProjectUpdateResponse> => {
          const isUpdate = item.nonProjectRequestId > 0;
          const url = isUpdate
            ? urlProvider.grmUpdateNonProjectUrl(item.nonProjectRequestId)
            : urlProvider.grmCreateNonProjectUrl();
          const method = isUpdate ? 'patch' : 'post';

          // Build PascalCase payload matching GRM Calendar API contract exactly.
          // When recurrenceEndsBy is not set, fall back to the event EndDateTime
          // (not today's date) to avoid 400 when creating future recurring events.
          const isRecurrenceEndDate = item.isRecurrent
            ? (item.isRecurrenceEndDate !== undefined
                ? !!item.isRecurrenceEndDate
                : !((item.recurrenceEndsAfter || 0) > 0))
            : false;
          const normalizedRecurrenceEndsBy = item.isRecurrent && item.recurrenceEndsBy
            ? toGrmDateTime(item.recurrenceEndsBy)
            : toGrmDateTime(item.endDateTime);
          // Emit the occurrence count only in "Ends after" mode so a preserved
          // count cannot override an "Ends by" series (and vice versa).
          const normalizedRecurrenceEndsAfter = item.isRecurrent && !isRecurrenceEndDate
            ? (item.recurrenceEndsAfter || 0)
            : 0;
          const grmPayload: Record<string, unknown> = {
            Id: isUpdate ? item.nonProjectRequestId : 0,
            EventType: item.eventType,
            Comments: item.comments,
            StartDateTime: toGrmDateTime(item.startDateTime),
            EndDateTime: toGrmDateTime(item.endDateTime),
            ResourceAlias: item.resourceAlias,
            TimeZone: item.timeZone,
            IsBusy: item.isBusy,
            ShowAs: (item as any).showAs || (item.isBusy ? 'Busy' : 'Free'),
            IsRecurrent: item.isRecurrent,
            RecurrencePattern: item.isRecurrent ? (item.recurrencePattern || 'Daily') : 'None',
            RecurrenceEndsAfter: normalizedRecurrenceEndsAfter,
            RecurrenceEndsBy: normalizedRecurrenceEndsBy,
            IsRecurrenceEndDate: isRecurrenceEndDate,
            TechnologyId: null,
            TechnologyName: null,
            RequestId: null,
          };

          try {
            const response = (method === 'patch' && patchFn)
              ? await patchFn.call(httpService, url, grmPayload, headers)
              : await httpService.post(url, grmPayload, headers);
            const data = response?.data || response;
            if (data?.ErrorMessage || data?.ErrorCode) {
              return { responseCode: 'GRM601', statusMessage: JSON.stringify(data) };
            }
            return { responseCode: 'C600', statusMessage: 'Success' };
          } catch (err) {
            const errData = (err as any)?.response?.data || (err as any)?.data;
            if (errData?.ErrorMessage) {
              return { responseCode: 'GRM601', statusMessage: JSON.stringify(errData) };
            }
            return { responseCode: 'GRM601', statusMessage: 'Failed to upsert non-project schedule.' };
          }
        };

        const results = await Promise.all(items.map(upsertOne));
        if (results.some(r => r.responseCode && r.responseCode !== 'C600')) {
          messageService.addMessage('One or more non-project schedules failed to upsert.', 'error');
        }
        return results;
      } catch (_error) {
        // Try to extract error details from the response
        const errData = (_error as any)?.response?.data || (_error as any)?.data;
        if (errData?.ErrorMessage) {
          return [{ responseCode: 'GRM601', statusMessage: JSON.stringify(errData) }];
        }
        messageService.addMessage('Failed to upsert non-project schedule.', 'error');
        return [];
      }
    };

    const deleteNonProjectSchedule = async (
      event: CalendarEvent,
      shouldDeleteSeries?: boolean
    ): Promise<NonProjectUpdateResponse[]> => {
      try {
        const cp = event.customProperties || {} as any;
        const id = cp.Id || event.eventId;
        // Use explicit shouldDeleteSeries if provided (from delete confirmation modal),
        // otherwise fall back to the event's IsRecurrent flag
        const deleteSeries = shouldDeleteSeries !== undefined ? shouldDeleteSeries : !!cp.IsRecurrent;

        // Convert dates to ISO format matching prod API: "2026-04-20T08:00:00.000Z"
        const toIsoDate = (dt: Date | string): string => {
          const d = dt instanceof Date ? dt : new Date(dt as any);
          // Adjust for timezone offset to preserve local time (same as GRM convertToDateTimeJSON)
          const local = new Date(d);
          local.setMinutes(d.getMinutes() - d.getTimezoneOffset());
          return local.toJSON();
        };

        const startDate = toIsoDate(event.startDt as any);
        const endDate = toIsoDate(event.endDt as any);

        // When deleting a series, use today as StartDate so past occurrences
        // (before today) are preserved — only future occurrences get deleted.
        // Combine today's date with the event's original start time so the
        // time portion matches what the API expects (e.g. 08:00:00).
        let effectiveStartDate = startDate;
        if (deleteSeries) {
          const eventStart = event.startDt instanceof Date
            ? event.startDt
            : new Date(event.startDt as any);
          const today = new Date();
          const todayWithEventTime = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            eventStart.getHours(),
            eventStart.getMinutes(),
            eventStart.getSeconds(),
            eventStart.getMilliseconds()
          );
          effectiveStartDate = toIsoDate(todayWithEventTime);
        }

        // Delete request body matching prod API payload
        const deletePayload = {
          NonProjectRequestId: Number(id),
          ShouldDeleteSeries: deleteSeries,
          StartDate: effectiveStartDate,
          EndDate: endDate,
        };

        const deleteUrl = urlProvider.grmRemoveNonProjectUrl();
        const headers = { 'Content-Type': 'application/json' };

        // FxpHttpService.delete(url, headers, data) — body goes as 3rd argument
        const response = await httpService.delete(deleteUrl, headers, deletePayload);

        const data = response?.data || response;

        // Prod API returns { Status: true } on success
        if (data?.Status === true || data?.status === true) {
          return [{ responseCode: 'C600', statusMessage: 'Success' }];
        }
        if (data?.ErrorMessage || data?.ErrorCode) {
          return [{ responseCode: 'GRM601', statusMessage: JSON.stringify(data) }];
        }
        // If we got a response without explicit Status or Error, treat as success
        // (backwards compat with any other response shapes)
        return [{ responseCode: 'C600', statusMessage: 'Success' }];
      } catch (_error) {
        const errData = (_error as any)?.response?.data || (_error as any)?.data;
        if (errData?.ErrorMessage) {
          return [{ responseCode: 'GRM601', statusMessage: JSON.stringify(errData) }];
        }
        messageService.addMessage('Failed to delete non-project schedule.', 'error');
        return [];
      }
    };

    return {
      fetchCalendarData,
      fetchNonProjectEventTypes,
      getUserPreference,
      saveUserPreference,
      upsertNonProjectSchedules,
      deleteNonProjectSchedule,
      buildUpsertPayloadFromOperation,
    };
  }

  return {
    getSingleton: function () {
      if (!_instance) {
        _instance = createInstance();
      }
      return _instance;
    },
    getNewInstance: function () {
      return createInstance();
    },
  };
})();

export default YearlyCalendarBridgeService;
